import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, api } from '../lib/api';
import { formatCurrency, formatCurrencyShort } from '../lib/utils';
import {
  Users, Wallet, PiggyBank, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell
} from 'recharts';

const AGEING_COLORS = { 'Current': '#1f1f1f', '30-60 Days': '#C5A059', '60-90 Days': '#D97706', '90+ Days': '#DC2626' };
const STAGE_COLORS = { 'Stage 1': '#059669', 'Stage 2': '#D97706', 'Stage 3': '#DC2626' };
const WATERFALL_COLORS = { 'Application': '#94A3B8', 'Under Review': '#C5A059', 'Approved': '#3B82F6', 'Disbursed': '#059669', 'Active': '#1f1f1f', 'Overdue': '#DC2626', 'Closed': '#6B7280', 'Rejected': '#991B1B' };

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [branchAgeing, setBranchAgeing] = useState([]);
  const [productDist, setProductDist] = useState([]);
  const [loanPhases, setLoanPhases] = useState([]);
  const [productPerf, setProductPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ageingView, setAgeingView] = useState('days'); // 'days' | 'stage'

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const safe = async (fn) => { try { const r = await fn(); return r.data; } catch { return null; } };
    const [stats, ba, pd, lp, pp] = await Promise.all([
      safe(dashboardApi.stats),
      safe(() => api.get('/dashboard/loans-by-branch-ageing')),
      safe(() => api.get('/dashboard/product-distribution')),
      safe(() => api.get('/dashboard/loans-by-phase')),
      safe(() => api.get('/dashboard/product-performance')),
    ]);
    if (stats) setStats(stats);
    setBranchAgeing(Array.isArray(ba) ? ba : []);
    setProductDist(Array.isArray(pd) ? pd : []);
    setLoanPhases(Array.isArray(lp) ? lp : []);
    setProductPerf(Array.isArray(pp) ? pp : []);
    setLoading(false);
  };

  const totalProducts = Array.isArray(productDist) ? productDist.reduce((s, d) => s + d.value, 0) : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-home">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">DASHBOARD</p>
        <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">Portfolio Overview</h1>
      </div>

      {/* Row 1: KPI Widgets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-5" data-testid="metric-total-clients">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-3xl font-bold text-[#1f1f1f]">{stats?.total_clients || 0}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Active Clients</p>
              {(stats?.total_groups > 0) && <p className="text-[10px] text-gray-400 mt-0.5">{stats.total_groups} Groups | {stats.group_member_count || 0} Group Members</p>}
            </div>
            <div className="p-2.5 bg-blue-50"><Users className="w-5 h-5 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="metric-loan-portfolio">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-3xl font-bold text-[#1f1f1f] truncate">{formatCurrencyShort(stats?.loan_portfolio || 0)}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Loan Portfolio</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{stats?.total_loans || 0} active | {stats?.pending_loans || 0} pending</p>
            </div>
            <div className="p-2.5 bg-emerald-50"><Wallet className="w-5 h-5 text-emerald-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="metric-total-deposits">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-3xl font-bold text-[#1f1f1f] truncate">{formatCurrencyShort(stats?.total_deposits || 0)}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Total Deposits</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{stats?.total_savings_accounts || 0} accounts</p>
            </div>
            <div className="p-2.5 bg-purple-50"><PiggyBank className="w-5 h-5 text-purple-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="metric-share-capital">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-3xl font-bold text-[#1f1f1f] truncate">{formatCurrencyShort(stats?.share_capital || 0)}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Share Capital</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{stats?.total_share_accounts || 0} accounts</p>
            </div>
            <div className="p-2.5 bg-amber-50"><Wallet className="w-5 h-5 text-amber-600" /></div>
          </div>
        </div>
      </div>

      {/* Row 2: Horizontal Bar Chart + Donut Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Loans by Branch and Ageing Status - spans 2 cols */}
        <div className="lg:col-span-2 bg-white border border-gray-200 p-6" data-testid="chart-branch-ageing">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#1f1f1f]">Loans by Branch — {ageingView === 'days' ? 'Day Ageing' : 'IFRS-9 Stage'}</h3>
            <div className="inline-flex border border-gray-200 rounded-sm overflow-hidden text-[10px]" data-testid="ageing-toggle">
              <button onClick={() => setAgeingView('days')} className={`px-3 py-1 ${ageingView === 'days' ? 'bg-[#1f1f1f] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-testid="ageing-toggle-days">Day Buckets</button>
              <button onClick={() => setAgeingView('stage')} className={`px-3 py-1 ${ageingView === 'stage' ? 'bg-[#1f1f1f] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-testid="ageing-toggle-stage">IFRS-9 Stages</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={branchAgeing} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis dataKey="branch" type="category" tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} width={100} />
              <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 0 }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {ageingView === 'days' ? (
                <>
                  <Bar dataKey="Current" stackId="a" fill={AGEING_COLORS['Current']} />
                  <Bar dataKey="30-60 Days" stackId="a" fill={AGEING_COLORS['30-60 Days']} />
                  <Bar dataKey="60-90 Days" stackId="a" fill={AGEING_COLORS['60-90 Days']} />
                  <Bar dataKey="90+ Days" stackId="a" fill={AGEING_COLORS['90+ Days']} />
                </>
              ) : (
                <>
                  <Bar dataKey="Stage 1" stackId="b" fill={STAGE_COLORS['Stage 1']} />
                  <Bar dataKey="Stage 2" stackId="b" fill={STAGE_COLORS['Stage 2']} />
                  <Bar dataKey="Stage 3" stackId="b" fill={STAGE_COLORS['Stage 3']} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Product Distribution Donut */}
        <div className="bg-white border border-gray-200 p-6" data-testid="chart-product-distribution">
          <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4">Product Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={productDist}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={88}
                dataKey="value"
                nameKey="name"
                label={({ name, value }) => {
                  const pct = totalProducts > 0 ? Math.round(value / totalProducts * 100) : 0;
                  if (pct < 5) return '';
                  const short = {
                    'Fixed Deposits': 'FD',
                    'Recurring Deposits': 'RD',
                    'Group Savings': 'Grp Sav',
                    'Group Loans': 'Grp Loans',
                  }[name] || name;
                  return `${short} ${pct}%`;
                }}
                labelLine={false}
                style={{ fontSize: 10 }}
              >
                {productDist.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 0 }} formatter={(v, name) => [`${v} accounts`, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center mt-2 text-[10px]" data-testid="product-dist-legend">
            {productDist.map((d, i) => {
              const short = { 'Fixed Deposits': 'FD', 'Recurring Deposits': 'RD', 'Group Savings': 'Grp Sav', 'Group Loans': 'Grp Loans' }[d.name] || d.name;
              return (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-[10px] text-gray-600">{short} ({d.value})</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 3: Waterfall Chart + Product Performance Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Loans by Phase — Waterfall Chart */}
        <div className="bg-white border border-gray-200 p-6" data-testid="chart-loans-phase">
          <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4">Loans by Phase and Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={loanPhases.filter(p => p.phase !== 'Total').map((p, i, arr) => {
                const prev = arr.slice(0, i).reduce((s, x) => s + x.count, 0);
                return { ...p, invisible: prev, visible: p.count };
              })}
              margin={{ bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="phase" tick={{ fontSize: 9, fill: '#6B7280' }} tickLine={false} angle={-35} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 0 }}
                formatter={(value, name) => name === 'invisible' ? null : [value, 'Loans']}
              />
              <Bar dataKey="invisible" stackId="a" fill="transparent" />
              <Bar dataKey="visible" stackId="a" name="Loans" radius={[2, 2, 0, 0]}>
                {loanPhases.filter(p => p.phase !== 'Total').map((entry, i) => (
                  <Cell key={i} fill={WATERFALL_COLORS[entry.phase] || '#1f1f1f'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {loanPhases.filter(p => p.phase !== 'Total' && p.count > 0).map((p, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: WATERFALL_COLORS[p.phase] + '20', color: WATERFALL_COLORS[p.phase] }}>
                {p.phase}: {p.count}
              </span>
            ))}
          </div>
        </div>

        {/* Product Performance Table - spans 2 cols */}
        <div className="lg:col-span-2 bg-white border border-gray-200" data-testid="table-product-performance">
          <div className="p-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#1f1f1f]">Product Performance</h3>
              <Link to="/dashboard/loans">
                <Button variant="ghost" size="sm" className="text-[#C5A059] text-xs">
                  View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Portfolio Value</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Avg Rate</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {productPerf.map((p, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{p.product || 'Unknown'}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-500 uppercase">{p.type}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-600 text-right">{p.active_accounts}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-600 text-right">{formatCurrency(p.portfolio_value)}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-600 text-right">{p.avg_rate}%</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
